import { StrictMode } from "react";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRoot } from "react-dom/client";

import "./index.css";

import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { z } from "zod";

import { Toasts, useToastStore } from "./components";
import { env } from "./env";
import { ThemeProvider } from "./providers/ThemeProvider";
import { Router } from "./router";

import "./i18n";

const errorSchema = z.object({ message: z.string() });

const isLocal = env.VITE_APP_ENV === "local";

const queryCache = !isLocal
  ? new QueryCache()
  : new QueryCache({
      // here we set a generic error handler on dev mode
      onError: (e, query) => {
        const parsedError = errorSchema.safeParse(e);
        const { pushToast } = useToastStore.getState();

        void pushToast({
          type: "error",
          title: "Error",
          message: `${query.queryKey[0] as string} error: ${
            !parsedError.success
              ? "Whoops! please check the network tab in the dev tools"
              : parsedError.data.message
          }`,
        });
      },
    });

const queryClient = new QueryClient({
  queryCache,
});

const root = document.getElementById("root");

if (!root) {
  throw new Error(
    "There's no #root div, something's wrong with our index.html",
  );
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <Router />
        </BrowserRouter>

        {ReactDOM.createPortal(<Toasts />, document.body)}

        {isLocal && <ReactQueryDevtools initialIsOpen={false} />}
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
